import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import { SimpleField } from "../../../components/fields/simpleField.jsx";
import { useInternet } from "../../../hooks/useInternet.js";
import { PromisePool } from "@supercharge/promise-pool";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PaginationTable } from "../../../components/table/paginationTable.jsx";
import { SubmitButton } from "../../../components/fields/submitButton.jsx";
import { Button } from "../../../components/button.jsx";
import { usePagination } from "../../../hooks/usePagination.js";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { contactLabel } from "../../../services/labels";
import { useResource } from "../../../hooks/useResource";

export function DocumentPage() {
  let session = useSelector((state) => state.session);
  let navigate = useNavigate();
  let documentsService = useResource("documents");

  let [search, setSearch] = useSearchParams();
  let pagination = usePagination({
    key: "document",
    meta: {
      sort: "-when",
      search: search.get("search") || "",
      filters: search.get("filters") || "",
    },
  });
  let internet = useInternet();

  let { control, handleSubmit, watch, setValue, formState, reset } = useForm({
    resolver: zodResolver(
      zod.object({
        search: zod.string(),
        filters: zod.string(),
      })
    ),
    defaultValues: {
      search: search.get("search") || "",
      filters: search.get("filters") || "",
    },
  });

  let [uploadProgress, setUploadProgress] = useState(null);

  const onDrop = useCallback(
    async (files) => {
      setUploadProgress({ i: 0, n: files.length });
      await PromisePool.withConcurrency(10)
        .for(files)
        .onTaskFinished((item, pool) => {
          setUploadProgress({
            i: pool.processedCount(),
            n: files.length,
          });
        })
        .process(async (file) => {
          let data = new FormData();
          data.append("file", file);
          let res = await internet.post("/documents", data);
          pagination.prepend([await res.json()]);
        });
      await setUploadProgress(null);
    },
    [pagination]
  );
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <div className={"w-full h-full flex flex-col gap-3 px-3 pt-3"}>
      <div className={"flex gap-3 items-center"}>
        <form
          onSubmit={handleSubmit((d) => {
            setSearch(d);
            pagination.setMeta(d);
            reset({}, { keepValues: true });
          })}
          className={"flex flex-col gap-3 w-full"}
        >
          <div className={"flex gap-3 flex-col md:flex-row"}>
            <div className={"flex gap-3 items-center w-full"}>
              <h2 className={"font-bold hidden md:block"}>Documents</h2>
              <SimpleField
                name={"search"}
                control={control}
                placeholder={"Search documents..."}
              />
              <SubmitButton formState={formState}>
                <span className={"fa fa-search"} />
              </SubmitButton>
              <Button
                onClick={() => {
                  navigate("/documents/new");
                }}
              >
                <span className={"fa fa-plus"} />
              </Button>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                {uploadProgress ? (
                  <p>
                    {uploadProgress.i} /{uploadProgress.n}
                  </p>
                ) : (
                  <span className={"fa fa-upload"} />
                )}
              </div>
              <Button
                onClick={async () => {
                  let file = await documentsService.collectionAction(
                    "export_zip",
                    {
                      ids: pagination.getData().map((x) => x.id),
                    }
                  );

                  window.open(
                    `${file.url}?token=${encodeURIComponent(
                      window.localStorage.token
                    )}`,
                    "blank"
                  );
                }}
              >
                <span className={"fa fa-download"} />
              </Button>
              <div className={"flex-grow"}></div>
            </div>
          </div>
        </form>
      </div>
      <PaginationTable
        resourceName={"documents"}
        pagination={pagination}
        onRowClick={(record) => navigate("/documents/" + record.id)}
        component={({ line, value, headerLabel, headerValue }) => (
          <div className={"pb-1"}>
            <div
              className={
                "bg-white cursor-pointer hover:bg-blue-100 rounded p-3 flex flex-col md:flex-row md:gap-3 border border-2 " +
                (line.transactions?.length
                  ? "border-green-300"
                  : "border-red-300")
              }
            >
              <div className={"font-semibold"}>
                {format(new Date(line.when), "yyyy-MM-dd")}
              </div>
              <div className={"flex-grow"}></div>
              {line.reference}
              {line.contact && (
                <div className={"opacity-40"}>{contactLabel(line.contact)}</div>
              )}
            </div>
          </div>
        )}
        noDataState={
          <p>
            <div
              className={"flex gap-5 items-center p-10 flex-col md:flex-row"}
            >
              <div
                className={
                  "bg-white flex items-center justify-center text-5xl "
                }
                style={{ borderRadius: "100%", minWidth: 200, minHeight: 200 }}
              >
                <span className={"fa fa-file-invoice text-blue-100"} />
              </div>
              <div>
                <strong>You don't have any document yet.</strong>
                <br />
                <br />
                Documents are your invoices and charges receipts.
                <br />
                <br />
                To add a document, please import data using the{" "}
                <span className={"fa fa-upload"} /> button.
              </div>
            </div>
          </p>
        }
        noResultState={
          <p>
            <div
              className={"flex gap-5 items-center p-10 flex-col md:flex-row"}
            >
              <div
                className={
                  "bg-white flex items-center justify-center text-5xl "
                }
                style={{ borderRadius: "100%", minWidth: 200, minHeight: 200 }}
              >
                <span className={"fa fa-search text-blue-100"} />
              </div>
              <div>
                <strong>No result found</strong>
                <br />
                <br />
                We didn't find any document matching your search. Please try
                searching with more broader terms.
              </div>
            </div>
          </p>
        }
      />
    </div>
  );
}
